import {
  download,
  FlexColumn,
  FlexRow,
  getFileNameFromS3Url,
  ImageThumbnail,
  StyledButton,
  useLoading,
  VideoThumbnail,
} from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { ChevronLeft, ChevronRight } from 'baseui/icon';
import { Modal, ModalBody, ModalFooter } from 'baseui/modal';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FILETYPE_PICTURE, FILETYPE_VIDEO } from '../../constants/app';
import { useStyles } from '../../hooks/useStyles';
import { MediaCore } from '../../types/media';
import { getMediaTypeLabel } from '../../utils/media';

type Props = {
  onClose: () => void;
  isOpen: boolean;
  mediaFiles: MediaCore[];
  selectedItemIndex: number;
};

function MediaPreviewModal({ onClose, isOpen, mediaFiles, selectedItemIndex }: Props) {
  const { theme, css, concatenateClasses, classes } = useStyles();
  const { startLoading, stopLoading, loading } = useLoading(false);
  const [current, setCurrent] = useState(selectedItemIndex);
  const [canShowVideo, setCanShowVideo] = useState(false);
  const media = mediaFiles[current];
  const hasMultipleFiles = mediaFiles.length > 1;

  useEffect(() => {
    if (hasMultipleFiles) {
      setTimeout(() => {
        const elem = document.getElementById(`media-${current}`);
        elem?.scrollIntoView();
      }, 0);
    }
  }, [current]);

  useEffect(() => {
    if (media.fileType === FILETYPE_VIDEO) {
      setCanShowVideo(false);
      setTimeout(() => {
        setCanShowVideo(true);
      }, 0);
    }
  }, [media]);

  const controlStyles = css({
    position: 'absolute',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    width: '100px',
    justifyContent: 'center',
    cursor: 'pointer',
    background: 'transparent',
    color: theme.colors.contentInversePrimary,
  });
  const mediaStyles = css({ height: '100%', maxWidth: '100%', objectFit: 'contain' });
  const modalBodyStyles = css({ display: 'flex', justifyContent: 'center', flex: 1 });
  const modalFooterStyles = css({
    marginLeft: '0 !important',
    marginRight: '0 !important',
    paddingLeft: theme.sizing.scale800,
    paddingRight: theme.sizing.scale800,
    borderTop: `${theme.borders.border200.borderWidth} ${theme.borders.border200.borderStyle} ${theme.borders.border200.borderColor}`,
  });

  const moveCurrent = (to: 'forward' | 'backward') => {
    const maxMediaIndex = mediaFiles.length - 1;
    if (to === 'forward') {
      if (current + 1 <= maxMediaIndex) {
        setCurrent((prev) => prev + 1);
      } else {
        setCurrent(0);
      }
      return;
    }
    if (current - 1 >= 0) {
      setCurrent((prev) => prev - 1);
    } else {
      setCurrent(maxMediaIndex);
    }
  };

  const downloadMedia = async () => {
    if (!media.url) return;
    startLoading();
    const fileName = getFileNameFromS3Url(media.url);
    if (fileName) download(media.url, fileName);
    stopLoading();
  };

  return createPortal(
    <Modal
      animate
      closeable
      onClose={onClose}
      isOpen={isOpen}
      size="full"
      overrides={{
        Dialog: {
          style: {
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <ModalBody className={modalBodyStyles}>
        <FlexColumn classNames={css({ overflow: 'hidden', width: '100%' })}>
          <FlexRow
            centered
            classNames={css({ flex: 1, position: 'relative', background: theme.colors.backgroundInversePrimary })}
          >
            {media.fileType === FILETYPE_PICTURE && (
              <img
                src={media.url}
                className={mediaStyles}
                alt=""
              />
            )}
            {media.fileType === FILETYPE_VIDEO && (
              <>
                {!canShowVideo && <div className={css({ width: '100%', height: '70vh' })} />}
                {canShowVideo && (
                  <video
                    className={mediaStyles}
                    controls
                    autoPlay
                  >
                    <source
                      src={media.url}
                      type="video/mp4"
                    />
                  </video>
                )}
              </>
            )}
            {hasMultipleFiles && (
              <>
                <Block
                  className={concatenateClasses(
                    controlStyles,
                    css({
                      right: '0',
                      ':hover': {
                        background: 'linear-gradient(270deg, #292929, transparent)',
                      },
                    }),
                  )}
                  onClick={() => moveCurrent('forward')}
                >
                  <ChevronRight
                    size={70}
                    style={{ paddingLeft: theme.sizing.scale600 }}
                  />
                </Block>
                <Block
                  className={concatenateClasses(
                    controlStyles,
                    css({
                      left: '0',
                      ':hover': {
                        background: 'linear-gradient(90deg, #292929, transparent)',
                      },
                    }),
                  )}
                  onClick={() => moveCurrent('backward')}
                >
                  <ChevronLeft
                    size={70}
                    style={{ paddingRight: theme.sizing.scale600 }}
                  />
                </Block>
              </>
            )}
          </FlexRow>
          {hasMultipleFiles && (
            <FlexRow classNames={css({ overflowX: 'auto' })}>
              {mediaFiles.map((mediaFile, index) => {
                if (!mediaFile.url) return null;

                return (
                  <div
                    id={`media-${index}`}
                    key={index}
                    className={css({
                      cursor: 'pointer',
                      opacity: index === current ? 1 : 0.7,
                      transition: 'opacity 0.3s ease',
                      minWidth: '200px',
                    })}
                    onClick={() => setCurrent(index)}
                  >
                    {mediaFile.fileType === FILETYPE_PICTURE && (
                      <ImageThumbnail
                        url={mediaFile.url}
                        imageStyle={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'contain',
                        }}
                      />
                    )}
                    {mediaFile.fileType === FILETYPE_VIDEO && (
                      <VideoThumbnail
                        url={mediaFile.url}
                        videoStyle={{ width: '100%', height: '100px', objectFit: 'contain' }}
                      />
                    )}
                  </div>
                );
              })}
            </FlexRow>
          )}
        </FlexColumn>
      </ModalBody>
      <ModalFooter className={modalFooterStyles}>
        <FlexRow
          gap={theme.sizing.scale300}
          classNames={concatenateClasses(classes.centeredEnd, css({ maxWidth: '500px', margin: '0 auto' }))}
        >
          <StyledButton
            kind="secondary"
            onClick={onClose}
            full
          >
            Cerrar vista de pantalla completa
          </StyledButton>
          <StyledButton
            onClick={downloadMedia}
            isLoading={loading}
            full
            disabled={!media.key}
          >
            Descargar {getMediaTypeLabel(media.key)}
          </StyledButton>
        </FlexRow>
      </ModalFooter>
    </Modal>,
    document.body,
  );
}

export default MediaPreviewModal;
